import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Building2,
  Calendar,
  CalendarCheck,
  ChevronDown,
  FileText,
  LayoutDashboard,
  PhoneCall,
  Shield,
  Users,
} from "lucide-react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { getCrmDashboard } from "../../api/crm/dashboard";

const STATUS_COLORS = ["#10b981", "#2563eb", "#f59e0b", "#ef4444", "#94a3b8"];
const LEAD_COLORS = ["#2563eb", "#0ea5e9", "#8b5cf6", "#14b8a6", "#f59e0b", "#64748b"];
const CONTRACT_STATUS_COLORS = ["#2563eb", "#ef4444", "#f59e0b", "#10b981", "#94a3b8"];

function formatNumber(value) {
  return Number(value || 0).toLocaleString("vi-VN");
}

function formatDateShort(value) {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("vi-VN");
}

function truncateText(value, maxLength = 80) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}…`;
}

function toMonthInputValue(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function EmptyChartState({ message }) {
  return (
    <div className="flex h-full items-center justify-center text-center text-sm font-semibold text-gray-400">
      {message}
    </div>
  );
}

function BreakdownCard({
  icon: Icon,
  iconClass,
  title,
  subtitle,
  data,
  colors,
  unit,
  loading,
  emptyMessage,
  delay = 0,
}) {
  const pieData = useMemo(
    () =>
      (data || []).map((item, index) => ({
        ...item,
        color: colors[index % colors.length],
      })),
    [data, colors]
  );

  return (
    <motion.div
      className="rounded-[2rem] border border-gray-100 bg-white p-6 shadow-sm"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay }}
    >
      <h2 className="mb-1 flex items-center gap-2 text-base font-black uppercase tracking-tight text-gray-800">
        <Icon className={iconClass} size={18} />
        {title}
      </h2>
      <p className="mb-5 text-[10px] font-bold uppercase tracking-widest text-gray-400">{subtitle}</p>
      {loading ? (
        <div className="h-[240px]">
          <EmptyChartState message="Đang tải biểu đồ..." />
        </div>
      ) : pieData.length === 0 ? (
        <div className="h-[240px]">
          <EmptyChartState message={emptyMessage} />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="h-[180px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={54}
                  outerRadius={82}
                  paddingAngle={5}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.key} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, _name, ctx) => [
                    `${formatNumber(value)} ${unit}`,
                    ctx?.payload?.label,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {pieData.map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="truncate text-sm font-semibold text-gray-700">{item.label}</span>
                </div>
                <span className="shrink-0 text-sm font-black text-gray-800">
                  {formatNumber(item.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function DashboardCRM() {
  const now = new Date();
  const navigate = useNavigate();

  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const monthPickerValue = toMonthInputValue(selectedYear, selectedMonth);

  const handleMonthChange = (event) => {
    const value = event.target.value;
    if (!value) return;
    const [year, month] = value.split("-").map(Number);
    if (!Number.isFinite(year) || !Number.isFinite(month)) return;
    setSelectedYear(year);
    setSelectedMonth(month);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await getCrmDashboard({
          year: selectedYear,
          month: selectedMonth,
          branch_id: selectedBranch,
        });
        if (!isMounted) return;
        setDashboard(data);
      } catch (err) {
        console.error("Failed to load CRM dashboard:", err);
        if (!isMounted) return;
        setDashboard(null);
        setError("Không thể tải dashboard CRM.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDashboard();

    return () => {
      isMounted = false;
    };
  }, [selectedYear, selectedMonth, selectedBranch]);

  const branchOptions = dashboard?.meta?.branches || [];
  const selectedBranchLabel = dashboard?.meta?.selectedBranchLabel || "Tất cả chi nhánh";
  const scopeLabel = dashboard?.meta?.scopeLabel || "";
  const showBranchFilter = !loading && (dashboard?.meta?.canPickBranch || false);
  const customerStatus = dashboard?.charts?.customerStatus || [];
  const leadSources = dashboard?.charts?.leadSources || [];
  const contractStatus = dashboard?.charts?.contractStatus || [];
  const recentActivities = dashboard?.recentActivities || [];

  const statsCards = useMemo(() => {
    const stats = dashboard?.stats;
    if (!stats) return [];

    return [
      {
        key: "customers",
        label: "Khách hàng",
        sub: `+${formatNumber(stats.customers.newThisMonth)} mới trong tháng`,
        value: formatNumber(stats.customers.total),
        icon: Users,
        valueClass: "text-sky-900",
        labelClass: "text-sky-800",
        iconWrapClass: "border border-sky-300 bg-white text-sky-800 shadow-sm",
        cardClass:
          "border-sky-300 bg-gradient-to-br from-sky-200 via-sky-100 to-blue-50 shadow-sky-200/60",
        blobClass: "bg-sky-400/55",
        watermarkClass: "text-sky-600/40",
        detailHref: "/crm/customers",
      },
      {
        key: "contracts",
        label: "Hợp đồng đang thực hiện",
        sub: `${formatNumber(stats.contracts.total)} tổng · ${formatNumber(
          stats.contracts.completed
        )} hoàn thành`,
        value: formatNumber(stats.contracts.active),
        icon: FileText,
        valueClass: "text-emerald-900",
        labelClass: "text-emerald-800",
        iconWrapClass:
          "border border-emerald-300 bg-white text-emerald-800 shadow-sm",
        cardClass:
          "border-emerald-300 bg-gradient-to-br from-emerald-200 via-emerald-100 to-teal-50 shadow-emerald-200/60",
        blobClass: "bg-emerald-400/55",
        watermarkClass: "text-emerald-600/40",
        detailHref: "/crm/contracts",
      },
      {
        key: "expiring",
        label: "Sắp hết hạn 30 ngày",
        sub: "Cần rà soát gia hạn",
        value: formatNumber(stats.contracts.expiringSoon),
        icon: AlertTriangle,
        valueClass: "text-amber-900",
        labelClass: "text-amber-800",
        iconWrapClass: "border border-amber-300 bg-white text-amber-800 shadow-sm",
        cardClass:
          "border-amber-300 bg-gradient-to-br from-amber-200 via-amber-100 to-orange-50 shadow-amber-200/60",
        blobClass: "bg-amber-400/55",
        watermarkClass: "text-amber-600/40",
        detailHref: "/crm/contracts",
      },
      {
        key: "contacts",
        label: "Lịch sử liên hệ",
        sub: `${formatNumber(stats.contacts.upcomingFollowups)} follow-up sắp tới`,
        value: formatNumber(stats.contacts.thisMonth),
        icon: PhoneCall,
        valueClass: "text-rose-900",
        labelClass: "text-rose-800",
        iconWrapClass: "border border-rose-300 bg-white text-rose-800 shadow-sm",
        cardClass:
          "border-rose-300 bg-gradient-to-br from-rose-200 via-rose-100 to-red-50 shadow-rose-200/60",
        blobClass: "bg-rose-400/55",
        watermarkClass: "text-rose-600/40",
        detailHref: "/crm/contact_history",
      },
    ];
  }, [dashboard]);

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
              <LayoutDashboard className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
                Tổng quan <span className="text-red-700">CRM</span>
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
                Khách hàng, hợp đồng và tương tác theo thời gian
              </p>
            </div>
          </div>
          {!loading && (scopeLabel || showBranchFilter) ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {scopeLabel ? (
                <div className="inline-flex items-center gap-2 rounded-2xl border border-red-100 bg-red-50/80 px-3 py-2 text-[11px] font-black uppercase tracking-wide text-red-800">
                  <Shield className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                  {scopeLabel}
                </div>
              ) : null}
              {showBranchFilter ? (
                <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-black uppercase tracking-wide text-slate-700">
                  <Building2 className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                  {selectedBranchLabel}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-3 lg:flex-nowrap lg:justify-end">
          {showBranchFilter ? (
            <div className="relative min-w-[14rem] flex-1 lg:flex-none">
              <div className="pointer-events-none absolute inset-y-0 left-4 z-10 flex items-center text-red-600">
                <Building2 size={16} strokeWidth={3} />
              </div>
              <select
                value={selectedBranch}
                onChange={(event) => setSelectedBranch(event.target.value)}
                className="h-[50px] w-full appearance-none rounded-2xl border border-gray-100 bg-white py-3 pl-11 pr-12 text-sm font-black text-gray-700 shadow-sm outline-none transition-all focus:border-red-200 focus:ring-2 focus:ring-red-100"
                aria-label="Chọn chi nhánh dashboard CRM"
              >
                <option value="all">Tất cả chi nhánh</option>
                {branchOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-gray-400">
                <ChevronDown size={16} strokeWidth={3} />
              </div>
            </div>
          ) : null}

          <div className="relative min-w-0 shrink-0">
            <div className="pointer-events-none absolute inset-y-0 left-4 z-10 flex items-center text-red-600">
              <Calendar size={16} strokeWidth={3} />
            </div>
            <input
              type="month"
              value={monthPickerValue}
              onChange={handleMonthChange}
              aria-label="Chọn tháng xem dashboard CRM"
              className="h-[50px] min-w-[11rem] rounded-2xl border border-gray-100 bg-white py-3 pl-11 pr-3 text-sm font-black uppercase tracking-wide text-gray-700 shadow-sm outline-none transition-all focus:border-red-200 focus:ring-2 focus:ring-red-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-[2rem] border border-red-100 bg-white px-6 py-4 text-sm font-semibold text-red-600 shadow-sm">
          {error}
        </div>
      ) : null}

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {loading ? (
          <div className="col-span-full py-10 text-center text-gray-500">Đang tải dashboard...</div>
        ) : (
          statsCards.map((item, index) => {
            const openDetail = () => {
              if (item.detailHref) navigate(item.detailHref);
            };

            return (
              <motion.div
                key={item.key}
                role={item.detailHref ? "link" : undefined}
                tabIndex={item.detailHref ? 0 : undefined}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={item.detailHref ? openDetail : undefined}
                onKeyDown={
                  item.detailHref
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openDetail();
                        }
                      }
                    : undefined
                }
                className={`group relative overflow-hidden rounded-[2rem] border p-6 shadow-md transition-all ${
                  item.cardClass
                } ${
                  item.detailHref
                    ? "cursor-pointer hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-red-200 focus-visible:ring-offset-2"
                    : ""
                }`}
              >
                <div
                  aria-hidden
                  className={`pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full blur-2xl ${item.blobClass}`}
                />
                <item.icon
                  aria-hidden
                  className={`pointer-events-none absolute -bottom-3 -right-2 h-24 w-24 ${item.watermarkClass}`}
                  strokeWidth={1.25}
                />
                <span className="pointer-events-none absolute right-4 top-3 z-10 text-[8px] font-black uppercase tracking-[0.2em] text-red-600 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                  Chi tiết
                </span>
                <div className="relative z-10 flex items-start justify-between">
                  <div
                    className={`rounded-2xl p-3 transition-transform group-hover:scale-110 ${item.iconWrapClass}`}
                  >
                    <item.icon size={24} />
                  </div>
                </div>
                <div className="relative z-10 mt-4">
                  <div className={`text-3xl font-black tracking-tight tabular-nums ${item.valueClass}`}>
                    {item.value}
                  </div>
                  <div
                    className={`mt-1 text-[10px] font-bold uppercase tracking-[0.2em] ${item.labelClass}`}
                  >
                    {item.label}
                  </div>
                  <div className="mt-0.5 text-[11px] font-semibold text-gray-600">{item.sub}</div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Breakdown charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <BreakdownCard
          icon={Users}
          iconClass="text-blue-600"
          title="Tình trạng khách hàng"
          subtitle="Phân bổ theo trạng thái CRM hiện tại"
          data={customerStatus}
          colors={STATUS_COLORS}
          unit="khách hàng"
          loading={loading}
          emptyMessage="Chưa có dữ liệu trạng thái khách hàng."
          delay={0}
        />
        <BreakdownCard
          icon={Building2}
          iconClass="text-sky-600"
          title="Nguồn khách hàng"
          subtitle="Kênh thu hút khách hàng đang quản lý"
          data={leadSources}
          colors={LEAD_COLORS}
          unit="khách hàng"
          loading={loading}
          emptyMessage="Chưa có dữ liệu nguồn khách hàng."
          delay={0.05}
        />
        <BreakdownCard
          icon={FileText}
          iconClass="text-emerald-600"
          title="Trạng thái hợp đồng"
          subtitle="Sức khỏe danh mục hợp đồng hiện tại"
          data={contractStatus}
          colors={CONTRACT_STATUS_COLORS}
          unit="hợp đồng"
          loading={loading}
          emptyMessage="Chưa có dữ liệu hợp đồng."
          delay={0.1}
        />
      </div>

      {/* Recent activities */}
      <motion.div
        className="rounded-[2rem] border border-gray-100 bg-white p-6 shadow-sm"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
      >
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-base font-black uppercase tracking-tight text-gray-800">
              <CalendarCheck className="text-emerald-600" size={18} />
              Hoạt động liên hệ gần đây
            </h2>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
              Lịch sử liên hệ / follow-up trong tháng đang xem
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/crm/contact_history")}
            className="inline-flex items-center justify-center rounded-full border border-red-100 bg-red-50 px-4 py-2 text-xs font-black uppercase tracking-wider text-red-700 transition hover:border-red-200 hover:bg-red-100"
          >
            Mở danh sách liên hệ
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] table-fixed border-collapse text-left">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="w-[22%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Khách hàng
                </th>
                <th className="w-[16%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Chi nhánh
                </th>
                <th className="w-[14%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Ngày liên hệ
                </th>
                <th className="w-[16%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Hình thức
                </th>
                <th className="w-[14%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Nhân viên
                </th>
                <th className="w-[18%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Ghi nhận
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-sm font-semibold text-gray-400">
                    Đang tải dữ liệu liên hệ...
                  </td>
                </tr>
              ) : recentActivities.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-sm font-semibold text-gray-400">
                    Không có hoạt động nào trong bộ lọc hiện tại.
                  </td>
                </tr>
              ) : (
                recentActivities.map((item) => (
                  <tr
                    key={item.id}
                    className="group cursor-pointer transition-colors hover:bg-gray-50/60"
                    onClick={() => navigate("/crm/contact_history")}
                  >
                    <td className="px-4 py-5 align-top">
                      <div className="font-black text-sm text-gray-800">{item.customer_name || "—"}</div>
                      <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        Follow-up: {formatDateShort(item.next_contact_date)}
                      </div>
                    </td>
                    <td className="px-4 py-5 align-top text-sm font-semibold text-gray-600">
                      {item.branch_name || "—"}
                    </td>
                    <td className="px-4 py-5 align-top text-sm font-semibold text-gray-600">
                      {formatDateShort(item.contact_date)}
                    </td>
                    <td className="px-4 py-5 align-top">
                      <span className="inline-flex rounded-full bg-red-50 px-3 py-1 text-xs font-black text-red-700">
                        {item.method || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-5 align-top text-sm font-semibold text-gray-600">
                      {item.staff_name || "—"}
                    </td>
                    <td className="px-4 py-5 align-top text-sm leading-6 text-gray-600">
                      <div className="font-semibold text-gray-700">{truncateText(item.result, 54)}</div>
                      <div className="mt-1 text-xs text-gray-500">{truncateText(item.content, 60)}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
